import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import downloadIcon from '@/assets/icons/download.svg';
import { downloadOrdersCsv } from '@/services/orders-export.service';
import { ordersService, type OrderDto } from '@/services/orders.service';
import { formatDateOnlyForDisplay } from '@/utils/date-only';
import { getPromoDiscountAmountInCents } from '@/utils/order-pricing';
import styles from './AdminOrderDetailsPanel.module.css';

type AdminOrderDetailsPanelProps = {
  order: OrderDto;
  passCount: number;
  statusLabel: string;
  statusClassName: string;
  domainNames: string;
  onClose: () => void;
  onOrderCancelled?: () => Promise<void> | void;
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
};

const formatDateOnly = (value?: string | null) => {
  return formatDateOnlyForDisplay(value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatMoney = (value?: { amount: number; currency: string } | null) => {
  if (!value) {
    return '-';
  }

  const amount = Number.isFinite(value.amount) ? value.amount : 0;
  const currency = value.currency || '';
  return `${(amount / 100).toFixed(2)} ${currency}`.trim();
};

const formatSyncStatus = (value?: string) => {
  if (!value) {
    return '-';
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
};

const formatMarginPercent = (value?: number) => {
  if (!Number.isFinite(value)) {
    return null;
  }

  return `${Math.trunc(value ?? 0)}%`;
};

export const AdminOrderDetailsPanel = ({
  order,
  passCount,
  statusLabel,
  statusClassName,
  domainNames,
  onClose,
  onOrderCancelled,
}: AdminOrderDetailsPanelProps) => {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isFullRefund, setIsFullRefund] = useState(false);
  const [refundPercentage, setRefundPercentage] = useState('');
  const showRefundPercentageOption = false;
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const discountAmount = getPromoDiscountAmountInCents(order);
  const discountCurrency = order.clientPrice?.currency || order.supplierPrice?.currency || '';
  const isDraftOrder = order.status === 'draft';
  const isCreatedOrder = order.status === 'created';
  const isPaidOrder = order.status === 'paid';
  const canCancelOrder = isDraftOrder || isCreatedOrder || isPaidOrder;
  const parsedRefundPercentage = useMemo(() => {
    if (!refundPercentage.trim()) {
      return null;
    }

    const normalized = Number.parseFloat(refundPercentage.replace(',', '.'));
    if (!Number.isFinite(normalized)) {
      return null;
    }

    return normalized;
  }, [refundPercentage]);
  const normalizedRefundPercentage = parsedRefundPercentage == null
    ? null
    : Math.min(100, Math.max(0, parsedRefundPercentage));
  const isRefundPercentageValid =
    normalizedRefundPercentage != null && normalizedRefundPercentage >= 0 && normalizedRefundPercentage <= 100;
  const paidWithCardInEur = (() => {
    if (order.paidWithCard != null) {
      return Math.max(0, Number(order.paidWithCard));
    }

    const fallbackClientPriceAmount = Number(order.clientPrice?.amount ?? 0);
    return fallbackClientPriceAmount > 0 ? fallbackClientPriceAmount : 0;
  })();
  const calculatedRefundAmountInEur = isFullRefund
    ? paidWithCardInEur
    : isRefundPercentageValid && normalizedRefundPercentage != null
      ? Math.round((paidWithCardInEur * normalizedRefundPercentage) / 100)
      : 0;
  const fullRefundAmountInEur = paidWithCardInEur;
  const canConfirmCancellation =
    isDraftOrder || isCreatedOrder || isPaidOrder || isFullRefund || isRefundPercentageValid;

  const closeCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
    setCancelError(null);
  }, []);

  const submitCancelOrder = useCallback(
    async (payload?: { fullRefund?: boolean; refundPercentage?: number }) => {
      setIsCancelling(true);
      setCancelError(null);

      try {
        await ordersService.cancelOrder(order.orderId, payload);
        await onOrderCancelled?.();
        onClose();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to cancel order';
        setCancelError(message);
      } finally {
        setIsCancelling(false);
      }
    },
    [onClose, onOrderCancelled, order.orderId]
  );

  const handleCancelOrderClick = useCallback(() => {
    if (!canCancelOrder || isCancelling) {
      return;
    }

    setIsFullRefund(false);
    setRefundPercentage('');
    setCancelError(null);
    setIsCancelModalOpen(true);
  }, [canCancelOrder, isCancelling]);

  const handleConfirmCancellation = useCallback(() => {
    if (!canConfirmCancellation || isCancelling) {
      return;
    }

    if (isDraftOrder || isCreatedOrder) {
      void submitCancelOrder();
      return;
    }

    if (isFullRefund) {
      void submitCancelOrder({ fullRefund: true });
      return;
    }

    if (isRefundPercentageValid && normalizedRefundPercentage != null) {
      void submitCancelOrder({ refundPercentage: normalizedRefundPercentage });
      return;
    }

    void submitCancelOrder({ refundPercentage: 0 });
  }, [
    canConfirmCancellation,
    isCreatedOrder,
    isDraftOrder,
    isCancelling,
    isFullRefund,
    isRefundPercentageValid,
    normalizedRefundPercentage,
    submitCancelOrder,
  ]);

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isCancelModalOpen) {
          closeCancelModal();
          return;
        }

        onClose();
      }
    },
    [closeCancelModal, isCancelModalOpen, onClose]
  );

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleEscape]);

  return createPortal(
    <>
      <button
        type="button"
        className={styles.backdrop}
        aria-label="Close order details"
        onClick={onClose}
      />
      <aside
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={`Order ${order.orderId} details`}
      >
        <div className={styles.header}>
          <div className={styles.meta}>ID: {order.orderId} | {order.reference || '-'}</div>
          <div className={styles.headingRow}>
            <div className={styles.headingWrap}>
              <h2 className={styles.title}>{passCount} Passes</h2>
              <span className={`${styles.badge} ${statusClassName}`}>{statusLabel}</span>
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={handleCancelOrderClick}
                disabled={!canCancelOrder || isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Cancel Order'}
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => downloadOrdersCsv([order], { isFiltered: true })}
              >
                <img src={downloadIcon} alt="Download" />
                Export
              </button>
              <button
                type="button"
                className={styles.closeButton}
                aria-label="Close order panel"
                onClick={onClose}
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <section className={styles.section}>
          <div className={styles.infoGrid}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Start Date</span>
              <span className={styles.fieldValue}>{formatDateOnly(order.startDate)}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Domain</span>
              <span className={styles.fieldValue}>{domainNames}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Email</span>
              <span className={styles.fieldValue}>
                {order.customerEmail || <span className={styles.emptyValue} />}
              </span>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.statsGrid}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Client Price</span>
              <span className={styles.statValue}>
                {order.clientPrice.amount ? formatMoney(order.clientPrice) : <span className={styles.emptyValue} />}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Supplier Price</span>
              <span className={styles.statValue}>
                {order.supplierPrice.amount ? formatMoney(order.supplierPrice) : <span className={styles.emptyValue} />}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Refund</span>
              <span className={styles.statValue}>
                {order.refundedAmount?.amount
                  ? formatMoney(order.refundedAmount)
                  : <span className={styles.emptyValue} />}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Margin</span>
              <span className={styles.statValue}>
                {order.margin.amount ? formatMoney(order.margin) : <span className={styles.emptyValue} />}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Promocode</span>
              <span className={styles.statValue}>
                {order.promoCode ? (
                  <span className={styles.promoCodePill}>{order.promoCode}</span>
                ) : (
                  <span className={styles.emptyValue} />
                )}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Discount</span>
              <span className={styles.statValue}>
                {discountAmount > 0
                  ? formatMoney({ amount: discountAmount, currency: discountCurrency })
                  : <span className={styles.emptyValue} />}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Applied Margin %</span>
              <span className={styles.statValue}>
                {formatMarginPercent(order.pricingMarginPercent) ?? <span className={styles.emptyValue} />}
              </span>
            </div>
            {order.paidWithMams ? (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Paid with MAMs</span>
                <span className={styles.statValue}>
                  {formatMoney({ amount: order.paidWithMams, currency: discountCurrency })}
                </span>
              </div>
            ) : null}
            {order.paidWithCard ? (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Paid with Card</span>
                <span className={styles.statValue}>
                  {formatMoney({ amount: order.paidWithCard, currency: discountCurrency })}
                </span>
              </div>
            ) : null}
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Ski Passes</h3>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.tableHead}>#</th>
                  <th className={styles.tableHead}>Ski Pass ID</th>
                  <th className={styles.tableHead}>Type</th>
                  <th className={styles.tableHead}>Ski Days</th>
                  <th className={styles.tableHead}>Start Date</th>
                  <th className={styles.tableHead}>Domain (Ski Area)</th>
                  <th className={styles.tableHead}>Pass Price</th>
                  <th className={styles.tableHead}>Supplier Price</th>
                  <th className={styles.tableHead}>Margin</th>
                  <th className={styles.tableHead}>First Name</th>
                  <th className={styles.tableHead}>Last Name</th>
                  <th className={styles.tableHead}>Date-of-birth</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, index) => {
                  const isRefunded = String(order.status) === 'refunded';

                  return (
                    <tr key={`${order.orderId}-${item.skipassId}-${index}-panel`}>
                      <td className={styles.tableCell}>{index + 1}</td>
                      <td className={styles.tableCell}>{item.skipassId || <span className={styles.emptyValue} />}</td>
                      <td className={styles.tableCell}>{item.ageCategory || <span className={styles.emptyValue} />}</td>
                      <td className={styles.tableCell}>{item.days}</td>
                      <td className={styles.tableCell}>{formatDateOnly(item.startDate)}</td>
                      <td className={styles.tableCell}>{item.domain.name || <span className={styles.emptyValue} />}</td>
                      <td className={`${styles.tableCell} ${isRefunded ? styles.refundedPrice : ''}`}>
                        {isRefunded ? formatMoney(item.skiPassPrice) : <span className={styles.emptyValue} />}
                      </td>
                      <td className={styles.tableCell}>
                        {item.supplierPrice.amount ? formatMoney(item.supplierPrice) : <span className={styles.emptyValue} />}
                      </td>
                      <td className={styles.tableCell}>
                        {item.margin.amount ? formatMoney(item.margin) : <span className={styles.emptyValue} />}
                      </td>
                      <td className={styles.tableCell}>{item.firstName || <span className={styles.emptyValue} />}</td>
                      <td className={styles.tableCell}>{item.lastName || <span className={styles.emptyValue} />}</td>
                      <td className={styles.tableCell}>
                        {item.birthDate ? formatDateOnly(item.birthDate) : <span className={styles.emptyValue} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Activity Log</h3>
          <div className={styles.logCard}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Created At</span>
              <span className={styles.fieldValue}>{formatDate(order.createdAt)}</span>
            </div>
            {order.paidAt ? (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Paid At</span>
                <span className={styles.fieldValue}>{formatDate(order.paidAt)}</span>
              </div>
            ) : null}
          </div>
        </section>

        {order.b2bSyncs?.length ? (
          <section className={styles.section}>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.tableHead}>B2B Order ID</th>
                    <th className={styles.tableHead}>Status</th>
                    <th className={styles.tableHead}>Attempted At</th>
                    <th className={styles.tableHead}>Synced At</th>
                    <th className={styles.tableHead}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {order.b2bSyncs.map((sync, index) => (
                    <tr key={`${order.orderId}-b2b-${sync.domainId}-${index}`}>
                      <td className={styles.tableCell}>{sync.b2bOrderId || <span className={styles.emptyValue} />}</td>
                      <td className={styles.tableCell}>{formatSyncStatus(sync.status)}</td>
                      <td className={styles.tableCell}>{sync.attemptedAt ? formatDate(sync.attemptedAt) : <span className={styles.emptyValue} />}</td>
                      <td className={styles.tableCell}>{sync.syncedAt ? formatDate(sync.syncedAt) : <span className={styles.emptyValue} />}</td>
                      <td className={styles.tableCell}>{sync.error || <span className={styles.emptyValue} />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        {order.freshdeskTicketId ? (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Freshdesk Ticket</h3>
            <div className={styles.logCard}>
              <div className={styles.field}>
                <a
                  href={`https://alps2alps.freshdesk.com/a/tickets/${order.freshdeskTicketId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  {order.freshdeskTicketId}
                </a>
              </div>
            </div>
          </section>
        ) : null}

        {cancelError ? <p className={styles.cancelError}>{cancelError}</p> : null}
      </aside>

      {isCancelModalOpen ? (
        <>
          <button
            type="button"
            className={styles.cancelModalBackdrop}
            aria-label="Close cancel confirmation"
            onClick={closeCancelModal}
          />
          <div className={styles.cancelModal} role="dialog" aria-modal="true" aria-label="Cancel order confirmation">
            <h3 className={styles.cancelModalTitle}>Cancel Order</h3>
            {isPaidOrder ? (
              <>
                <p className={styles.cancelModalText}>
                  Are you sure you want to cancel this order? Please select refund conditions to proceed with cancellation.
                </p>

                <label className={styles.cancelCheckboxRow}>
                  <input
                    type="checkbox"
                    checked={isFullRefund}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setIsFullRefund(checked);
                      if (checked) {
                        setRefundPercentage('');
                      }
                    }}
                  />
                  <span>
                    Full refund ({formatMoney({ amount: fullRefundAmountInEur, currency: 'EUR' })})
                  </span>
                </label>

                {showRefundPercentageOption ? (
                  <>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      className={styles.cancelPercentageInput}
                      placeholder="Set the % of the EUR paid Order price to refund"
                      value={refundPercentage}
                      disabled={isFullRefund}
                      onChange={(event) => {
                        const nextRaw = event.target.value;
                        if (!nextRaw.trim()) {
                          setRefundPercentage('');
                          return;
                        }

                        const nextParsed = Number.parseFloat(nextRaw.replace(',', '.'));
                        if (!Number.isFinite(nextParsed)) {
                          setRefundPercentage(nextRaw);
                          return;
                        }

                        const clamped = Math.min(100, Math.max(0, nextParsed));
                        setRefundPercentage(String(clamped));
                      }}
                    />

                    <p className={styles.refundHintText}>
                      Refund amount: {formatMoney({ amount: calculatedRefundAmountInEur, currency: 'EUR' })}
                    </p>
                  </>
                ) : null}
              </>
            ) : (
              <p className={styles.cancelModalText}>
                Are you sure you want to delete this order? This action cannot be undone.
              </p>
            )}

            {cancelError ? <p className={styles.cancelError}>{cancelError}</p> : null}

            <div className={styles.cancelModalActions}>
              <button
                type="button"
                className={styles.cancelModalSecondaryButton}
                onClick={closeCancelModal}
                disabled={isCancelling}
              >
                Back
              </button>
              <button
                type="button"
                className={styles.cancelModalPrimaryButton}
                onClick={handleConfirmCancellation}
                disabled={!canConfirmCancellation || isCancelling}
              >
                {isCancelling ? 'Processing...' : isPaidOrder ? 'Confirm cancellation' : 'Confirm delete'}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </>,
    document.body
  );
};
